import { convertPersianNumbersToEnglishNumbers } from '../../helpers/numbers'
import {
  PaymentFollowUp,
  PaymentFollowUpAction,
  PaymentSettlementInput,
  PaymentSettlementOutcome
} from '../../types/paymentFollowUps'

export interface SettlementForm {
  action: PaymentFollowUpAction | ''
  outcome: PaymentSettlementOutcome
  amount: string
  reference: string
  verifiedAt: string
  fullSettlement: boolean
  payerAcceptanceReference: string
  walletTransactionReference: string
}

export const scopedSettlement = (followUp: PaymentFollowUp): boolean =>
  followUp.projectionContract === 'LiabilityCoverage'

export const projectionPending = (followUp: PaymentFollowUp): boolean =>
  followUp.projectionContract != null &&
  (!scopedSettlement(followUp) || followUp.projectionComplete !== true)

export function settlementOutcomes(
  followUp: PaymentFollowUp,
  action = followUp.requiredAction
): PaymentSettlementOutcome[] {
  if (
    scopedSettlement(followUp) &&
    !followUp.outstandingByAction?.some((row) => row.action === action)
  )
    return []
  switch (scopedSettlement(followUp) ? action : followUp.requiredAction) {
    case 'ReviewRefund':
      return [
        'GatewayRefund',
        'AcceptedWalletSettlement',
        'ReviewedNoFurtherAction'
      ]
    case 'ReviewProviderReversal':
      return ['ProviderReversal', 'ReviewedNoFurtherAction']
    case 'ReviewWalletCredit':
      return scopedSettlement(followUp)
        ? ['WalletCreditConfirmed', 'ReviewedNoFurtherAction']
        : ['AcceptedWalletSettlement', 'ReviewedNoFurtherAction']
    case 'ReviewProviderConflict':
      return ['ReviewedNoFurtherAction']
    default:
      return []
  }
}

export function initialSettlement(
  followUp: PaymentFollowUp,
  preferredAction?: PaymentFollowUpAction
): SettlementForm {
  const now = new Date()
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
  const action =
    preferredAction ??
    followUp.outstandingByAction?.[0]?.action ??
    followUp.requiredAction ??
    ''
  const balance = scopedSettlement(followUp)
    ? followUp.outstandingByAction?.find((row) => row.action === action)
    : null
  const amount = scopedSettlement(followUp)
    ? balance && !balance.hasUnknownAmount
      ? balance.knownAmount
      : null
    : followUp.amountUnderReview
  return {
    action,
    outcome:
      settlementOutcomes(followUp, action || undefined)[0] ??
      'ReviewedNoFurtherAction',
    amount: amount == null ? '' : String(amount),
    reference: '',
    verifiedAt: local.toISOString().slice(0, 16),
    fullSettlement: false,
    payerAcceptanceReference: '',
    walletTransactionReference: ''
  }
}

/** Strict grouping/precision: stripping arbitrary punctuation could change a money amount. */
export function parseSettlementAmount(
  value: string,
  currency: string | null
): number | null {
  if (!currency || !['IRR', 'USD', 'EUR'].includes(currency)) return null
  const normalized = convertPersianNumbersToEnglishNumbers(value.trim())
    .replace(/[٠-٩]/g, (digit) => String(digit.charCodeAt(0) - 0x660))
    .replace(/٫/g, '.')
  const pattern =
    currency === 'IRR'
      ? /^(?:\d+|\d{1,3}(?:[,٬]\d{3})+)$/
      : /^(?:\d+|\d{1,3}(?:[,٬]\d{3})+)(?:\.\d{1,2})?$/
  if (!pattern.test(normalized)) return null
  const amount = Number(normalized.replace(/[,٬]/g, ''))
  const scale = currency === 'IRR' ? 1 : 100
  return Number.isFinite(amount) &&
    amount > 0 &&
    Number.isSafeInteger(Math.round(amount * scale))
    ? amount
    : null
}

const validReference = (value: string) =>
  Array.from(value.trim()).length >= 3 && Array.from(value.trim()).length <= 200

/** Null means incomplete/invalid; backend revision and monetary guards remain authoritative. */
export function settlementInput(
  followUp: PaymentFollowUp,
  form: SettlementForm
): PaymentSettlementInput | null {
  if (
    followUp.caseSchemaVersion !== 2 ||
    !Number.isInteger(followUp.revision) ||
    !followUp.revision ||
    followUp.revision < 1 ||
    followUp.state !== 'Open' ||
    projectionPending(followUp) ||
    !settlementOutcomes(followUp, form.action || undefined).includes(
      form.outcome
    ) ||
    !validReference(form.reference)
  )
    return null
  const date = new Date(form.verifiedAt)
  if (!Number.isFinite(date.getTime()) || date.getTime() > Date.now())
    return null
  const reviewOnly = form.outcome === 'ReviewedNoFurtherAction'
  const amount = reviewOnly
    ? null
    : parseSettlementAmount(form.amount, followUp.currency)
  if (!reviewOnly && (amount === null || amount > followUp.amount)) return null
  const scope = scopedSettlement(followUp)
    ? followUp.outstandingByAction?.find((row) => row.action === form.action)
    : null
  if (scopedSettlement(followUp) && !scope) return null
  const balance = scope ? scope.knownAmount : followUp.amountUnderReview
  if (!reviewOnly && scope?.hasUnknownAmount && form.fullSettlement) return null
  if (!reviewOnly && !scope?.hasUnknownAmount && balance != null) {
    const scale = followUp.currency === 'IRR' ? 1 : 100
    const remaining =
      Math.round(balance * scale) - Math.round((amount as number) * scale)
    if (remaining < 0 || form.fullSettlement !== (remaining === 0)) return null
  }
  if (
    form.outcome === 'AcceptedWalletSettlement' &&
    !validReference(form.payerAcceptanceReference)
  )
    return null
  if (
    form.outcome === 'WalletCreditConfirmed' &&
    !validReference(form.walletTransactionReference)
  )
    return null
  return {
    expectedRevision: followUp.revision,
    outcome: form.outcome,
    reference: form.reference.trim(),
    verifiedAt: date.toISOString(),
    ...(scope
      ? {
          coverage: [
            {
              action: scope.action,
              amount,
              fullActionSettlement: reviewOnly || form.fullSettlement
            }
          ]
        }
      : { amount, fullSettlement: reviewOnly || form.fullSettlement }),
    ...(form.outcome === 'AcceptedWalletSettlement'
      ? { payerAcceptanceReference: form.payerAcceptanceReference.trim() }
      : {}),
    ...(form.outcome === 'WalletCreditConfirmed'
      ? { walletTransactionReference: form.walletTransactionReference.trim() }
      : {})
  }
}
